import type { actions } from '@luxuryescapes/contract-svc-lab-v2'
import request, { authOptions } from 'api/requestUtils'
import config from 'constants/config'
import type { LabV2Config, LabV2Ui } from 'lib/labV2/evaluation'

const EVALUATE_PATH = '/api/lab/v2/evaluate'
const EVALUATE_TIMEOUT_MS = 1000

type EvaluateResponse = actions['evaluateExperiments']['responses'][200]['content']['application/json']

export type LabV2Evaluation = {
  configs: Record<string, unknown>
  uis: Array<LabV2Ui>
}

/**
 * Config payloads keyed by name, plus the qualifying UI rows in campaign priority order.
 * Undefined when the call is skipped, fails, or the body is not an evaluate result.
 */
export async function evaluateLabV2(
  token: string | undefined,
  enabled: boolean,
  attributes: Record<string, string>,
): Promise<LabV2Evaluation | undefined> {
  if (!enabled || !config.LAB_V2_ENABLED) {
    return undefined
  }

  try {
    const body = await request.post<EvaluateResponse, { attributes: Record<string, string> }>(
      EVALUATE_PATH,
      { attributes },
      {
        ...authOptions(token),
        timeout: EVALUATE_TIMEOUT_MS,
        // The evaluate body only allows `attributes`. This keeps the shared client from adding `brand`.
        excludeBrand: true,
      },
    )
    return evaluationFromBody(body)
  } catch {
    // The document still renders when Lab v2 is down or slow.
    return undefined
  }
}

function evaluationFromBody(body: EvaluateResponse): LabV2Evaluation | undefined {
  if (!Array.isArray(body?.result?.configs) || !Array.isArray(body.result.uis)) {
    return undefined
  }

  return {
    configs: configsByName(body.result.configs),
    uis: body.result.uis,
  }
}

function configsByName(configs: Array<LabV2Config>): Record<string, unknown> {
  const named: Record<string, unknown> = {}
  for (const config of configs) {
    if (config.name.length === 0 || config.name in named) {
      continue
    }
    named[config.name] = config.payload
  }
  return named
}
